import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ChevronDown, ChevronUp, Sparkles, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTourStore } from '@/features/guided-tour/store/tour-store';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { esEmpresaDemo } from '../empresa-demo';
import { EVENTO_TIENDA_DE_LA_PRUEBA, demoService, type EstadoDeLaDemo } from '../demo-service';
import {
  TEXTOS_DE_ELEGIR_TIENDA,
  faltaElegirTienda,
  nombreDeLaPrueba,
} from '../textos-del-estado-de-la-demo';
import { diasQueQuedan } from '@/lib/utils/dias-que-quedan';
import { MISIONES, type Mision } from '../misiones';
import {
  ESTADO_EN_PALABRAS,
  misionPrevia,
  textoDePrimero,
  vistaDeMision,
} from '../estado-de-mision';
import {
  SIN_PROGRESO,
  empresaDeLaSesion,
  misionesHechas,
  useProgresoDeLaEmpresa,
  useProgresoDeMisiones,
  useRecienCumplida,
} from '../progreso-de-misiones';
import { useEscucharMisionesCumplidas } from '../acciones-de-misiones';
import { logoDePrixia } from '../animaciones/elegir-logo';
import { modoDeEscena } from '../animaciones/modo-de-animacion';
import { piezaQueEntra } from '../animaciones/piezas-del-logo';
import { LogoDeMisiones } from './logo-de-misiones';
import { cargarEscenaDeMision, cargarEscenaDeMomento } from '../animaciones/cargar-escena';
import {
  marcarMomento,
  puedeVerseMomento,
  quedaMomentoPorVer,
} from '../animaciones/momentos-unicos';
import { CapaDeMomento } from './capa-de-momento';
import { PanelDeMision } from './panel-de-mision';

/**
 * Mini Prixia: «Tus misiones en Prixia», la tarjeta flotante que guía la prueba.
 *
 * Sólo existe para una empresa demo. Cada misión lleva a su pantalla y arranca el
 * tour de esa pantalla, siempre (también las cumplidas: decisión 21). Se tilda sola
 * cuando la persona hace lo que pide el último paso (X3, decisión 22); el avance vive
 * en el servidor. Orden sugerido: si falta la misión previa, avisa «Primero …».
 */
/**
 * Recorrido 3-nov: la cabecera decía «15 días sin costo» fijo aunque quedaran 5. Dice lo que
 * queda de la prueba; los 15 días van en la invitación, antes de empezar. Sin fecha, no
 * inventa un plazo.
 */
/** Decisión 1 del 3-nov: donde se elige la tienda de la prueba. */
const RUTA_DE_ELEGIR_TIENDA = '/compite/competencia';

/** El alta entra a elegir la tienda (`?bienvenida=1` en Competencia): ahí no arranca la misión 1. */
const llegaAElegirTienda = (ruta: string) =>
  ruta.replace(/\/+$/, '').endsWith(RUTA_DE_ELEGIR_TIENDA);

function plazoDeLaPrueba(dias: number | null): string {
  if (dias === null) return 'sin costo';
  if (dias < 0) return 'sin costo, ya venció';
  if (dias === 0) return 'sin costo, vence hoy';
  return `sin costo, te ${dias === 1 ? 'queda 1 día' : `quedan ${dias} días`}`;
}

export function MisionesDemo() {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = useTourStore((s) => s.isActive);
  const currentTourId = useTourStore((s) => s.currentTourId);
  const startTour = useTourStore((s) => s.startTour);
  const progreso = useProgresoDeLaEmpresa();
  const empezar = useProgresoDeMisiones((s) => s.empezar);
  const cargar = useProgresoDeMisiones((s) => s.cargar);
  const diasDeLaPrueba = diasQueQuedan(useAuthStore((s) => s.subscription?.endDate));
  // Orden sugerido: la misión elegida y la que le falta antes.
  const [primero, setPrimero] = useState<{ mision: Mision; previa: Mision } | null>(null);
  useEscucharMisionesCumplidas();
  // Ronda V (V4): plegada, sólo la cabecera con el progreso; abierta tapaba listas y botones.
  const [abierta, setAbierta] = useState(false);
  const recien = useRecienCumplida();
  const olvidarRecien = useProgresoDeMisiones((s) => s.olvidarRecienCumplida);
  // El total de la misión que se está animando en el panel flotante.
  const [panel, setPanel] = useState<number | null>(null);
  // Momento 3 (spec 344): el logo que se abre al llegar por primera vez.
  const [ingreso, setIngreso] = useState(false);
  /** El estado de la prueba (servidor): si falta la tienda y cómo se llama la prueba. */
  const [estadoDemo, setEstadoDemo] = useState<EstadoDeLaDemo | null>(null);
  const sinTienda = faltaElegirTienda(estadoDemo);

  const tour = { isActive, currentTourId };
  const hechas = misionesHechas(progreso);
  const siguiente = MISIONES.find((m) => !progreso.hechas.includes(m.id));

  /*
    Abre la pantalla de la misión y (re)lanza su recorrido desde el primer paso. Arranca
    ya: el proveedor lo muestra cuando la pantalla cargó SU recorrido (X3c, sólo demo).
  */
  const arrancar = (m: Mision, reemplazar = false) => {
    // Soporte («Entrar como») sólo mira: no escribe ni arranca recorridos de la persona.
    if (useAuthStore.getState().isImpersonating) return;
    // Al elegir una misión se pliega: el tour que arranca no queda tapado.
    setAbierta(false);
    setPrimero(null);
    empezar(m.id);
    navigate(m.ruta, { replace: reemplazar });
    startTour(m.tour);
  };

  /** Decisión 1 del 3-nov: a «Tiendas Prixia», a elegir la tienda de la prueba. */
  const elegirTienda = () => {
    setAbierta(false);
    navigate(RUTA_DE_ELEGIR_TIENDA);
  };

  // Orden sugerido (respuesta de Sebastián): sin la previa cumplida, primero se avisa.
  const ir = (m: Mision) => {
    if (useAuthStore.getState().isImpersonating) return;
    const previa = misionPrevia(m, progreso);
    if (!previa) return arrancar(m);
    setAbierta(false);
    navigate(m.ruta);
    setPrimero({ mision: m, previa });
  };

  /*
    Al montar en una demo: el avance que guardó el servidor, y el recorrido de la misión 1
    arranca solo en dos casos (decisión 21 y P3 de la Ronda Z):
    · recién creada la empresa (`?bienvenida=1`; su ruta no lleva `bienvenida`: al
      reemplazar, se va);
    · la demo quedó SIN ninguna misión empezada ni hecha, que es como la deja «Reiniciar demo».
    No se repite en cada ingreso porque `arrancar` anota la misión 1 como empezada en el
    servidor: desde ahí la condición ya no se cumple, ni en esta pestaña ni en otra. Si el
    servidor no contestó, no se arranca nada: sin saber el avance no se puede decir que esté en cero.
  */
  useEffect(() => {
    if (!esEmpresaDemo()) return;
    // Soporte de plataforma («Entrar como»): lee el avance real, pero mira, no recorre.
    // Arrancar gastaría la bienvenida de la persona, y la misión 1 quedaría anotada con la
    // sesión del equipo.
    if (useAuthStore.getState().isImpersonating) {
      void cargar();
      return;
    }
    const recienCreada = new URLSearchParams(location.search).get('bienvenida') === '1';
    let activo = true;
    const cargado = cargar();
    if (recienCreada) {
      // Acá y no después: `arrancar` saca `bienvenida` de la dirección.
      if (puedeVerseMomento('ingreso')) setIngreso(true);
      // Decisión 1 del 3-nov: la empresa nace sin tienda y el alta la trae a elegirla. Ahí no arranca la
      // misión 1 (no hay productos que mirar): sólo se saca `bienvenida` de la dirección.
      if (llegaAElegirTienda(location.pathname)) navigate(location.pathname, { replace: true });
      else arrancar(MISIONES[0], true);
      return;
    }
    // Sin tienda elegida tampoco arranca sola: primero se elige la tienda.
    void Promise.all([cargado, demoService.estado().catch(() => null)]).then(([cargo, estado]) => {
      if (!activo || !cargo || faltaElegirTienda(estado)) return;
      const p = useProgresoDeMisiones.getState().porEmpresa[empresaDeLaSesion()] ?? SIN_PROGRESO;
      if (p.empezadas.length === 0 && p.hechas.length === 0) arrancar(MISIONES[0], true);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    Lo recién cumplido se consume acá, una sola vez. Sólo se pide el 3D si hay una pieza
    que mostrar y se puede animar. Una misión sin pieza no corta el panel que esté abierto;
    el logo fijo sale de `hechas` y queda bien en cualquier caso.
  */
  useEffect(() => {
    if (recien === null) return;
    olvidarRecien();
    // Soporte («Entrar como») mira: no se le muestra la animación de la persona.
    if (useAuthStore.getState().isImpersonating) return;
    if (!esEmpresaDemo() || !piezaQueEntra(logoDePrixia(), recien)) return;
    if (modoDeEscena() === 'animada') setPanel(recien);
  }, [recien, olvidarRecien]);

  /*
    El estado de la prueba: al montar, al volver a la pestaña y cuando se elige o cambia la tienda
    (`EVENTO_TIENDA_DE_LA_PRUEBA`). Si no contesta, las misiones se ven como siempre.
  */
  useEffect(() => {
    if (!esEmpresaDemo()) return;
    let vivo = true;
    const pedir = () => {
      void demoService
        .estado()
        .then((e) => {
          if (vivo) setEstadoDemo(e);
        })
        .catch(() => {});
    };
    pedir();
    window.addEventListener('focus', pedir);
    window.addEventListener(EVENTO_TIENDA_DE_LA_PRUEBA, pedir);
    return () => {
      vivo = false;
      window.removeEventListener('focus', pedir);
      window.removeEventListener(EVENTO_TIENDA_DE_LA_PRUEBA, pedir);
    };
  }, []);

  // Otra pestaña pudo cumplir misiones: al volver se trae el avance, sin animar nada
  // (también en soporte, que lo lee sin escribir).
  useEffect(() => {
    const alVolver = () => {
      if (esEmpresaDemo()) void cargar();
    };
    window.addEventListener('focus', alVolver);
    return () => window.removeEventListener('focus', alVolver);
  }, [cargar]);

  /*
    Precarga del 3D, sólo en una demo que lo va a usar: en un rato libre baja el mismo módulo
    que usa el panel (`cargarEscenaDeMision` lo pide una sola vez). Si falla, el panel
    tiene su propia salida segura.
  */
  useEffect(() => {
    if (!esEmpresaDemo() || useAuthStore.getState().isImpersonating) return;
    if (modoDeEscena() !== 'animada') return;
    const pedir = () => {
      try {
        void Promise.resolve(cargarEscenaDeMision()).catch(() => {});
        if (quedaMomentoPorVer()) void Promise.resolve(cargarEscenaDeMomento()).catch(() => {});
      } catch {
        // Se ignora: el panel igual tiene su salida segura.
      }
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(pedir);
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(pedir, 2000);
    return () => window.clearTimeout(id);
  }, []);

  if (!esEmpresaDemo()) return null;
  const r = 16;
  const largo = 2 * Math.PI * r;

  return (
    /*
      Vault 349 (P2.1): en la PC flota junto al menú (268 px); en el teléfono es una barra a
      lo ancho, abajo, y al abrirse una hoja inferior con su propio desplazamiento. El
      contenido de la app ya deja 176 px abajo (`pb-44` del marco): la barra no tapa botones.
    */
    <div className="fixed inset-x-3 bottom-3 z-40 md:inset-x-auto md:bottom-5 md:left-[268px] md:w-[300px]">
      <AnimatePresence>
        {/* `fixed` adentro de la tarjeta: debajo del recorrido y de los diálogos, nunca encima. */}
        {ingreso && (
          <CapaDeMomento
            key="ingreso"
            momento="ingreso"
            className="fixed inset-0"
            alListo={() => marcarMomento('ingreso')}
            alCerrar={() => setIngreso(false)}
          />
        )}
        {panel !== null && (
          <PanelDeMision
            key={panel}
            enTotal={panel}
            abierta={abierta}
            alCerrar={() => setPanel((actual) => (actual === panel ? null : actual))}
          />
        )}
      </AnimatePresence>
      {/* Teléfono: con la hoja abierta, el fondo se oscurece y tocarlo la cierra. */}
      {abierta && (
        <div
          aria-hidden="true"
          data-fondo-de-misiones=""
          className="fixed inset-0 bg-black/40 md:hidden"
          onClick={() => setAbierta(false)}
        />
      )}
      {/* El panel va afuera: `overflow-hidden` lo recortaría. */}
      <div
        data-tour="demo-misiones"
        className="relative flex max-h-[85dvh] flex-col overflow-hidden rounded-2xl border bg-card shadow-xl md:block md:max-h-none"
      >
        <button
          type="button"
          onClick={() => setAbierta((a) => !a)}
          className="flex w-full shrink-0 items-center gap-3 px-4 py-3 text-left"
          aria-expanded={abierta}
        >
          <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
            <circle cx="20" cy="20" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="4" />
            <circle
              cx="20"
              cy="20"
              r={r}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={largo}
              strokeDashoffset={largo * (1 - hechas / MISIONES.length)}
              transform="rotate(-90 20 20)"
              style={{ transition: 'stroke-dashoffset .6s cubic-bezier(.2,.8,.2,1)' }}
            />
            <text
              x="20"
              y="24"
              textAnchor="middle"
              className="fill-foreground text-[12px] font-semibold"
            >
              {hechas}/{MISIONES.length}
            </text>
          </svg>
          <span className="flex-1">
            <span className="block text-sm font-semibold">Tus misiones en Prixia</span>
            <span className="block text-xs text-muted-foreground">
              {sinTienda
                ? TEXTOS_DE_ELEGIR_TIENDA.cabecera
                : `${nombreDeLaPrueba(estadoDemo)} · ${plazoDeLaPrueba(diasDeLaPrueba)}`}
            </span>
          </span>
          {abierta ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </button>
        <AnimatePresence initial={false}>
          {abierta && (
            <motion.div
              className="min-h-0 overflow-y-auto md:overflow-visible"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            >
              <LogoDeMisiones hechas={hechas} />
              {/* Con el logo, la tarjeta puede pasar del alto de la ventana: la lista se desplaza. */}
              <ol className="space-y-1 px-3 pb-2 md:max-h-[calc(100vh-17rem)] md:overflow-y-auto">
                {/* Decisión 1 del 3-nov: sin tienda elegida, el paso previo a las misiones. */}
                {sinTienda && (
                  <li>
                    <button
                      type="button"
                      onClick={elegirTienda}
                      className="flex min-h-11 w-full items-start gap-3 rounded-xl bg-primary/5 px-2 py-2 text-left ring-1 ring-primary/30 hover:bg-muted"
                    >
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 border-primary">
                        <Store className="h-3 w-3 text-primary" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium">
                          {TEXTOS_DE_ELEGIR_TIENDA.mision}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {TEXTOS_DE_ELEGIR_TIENDA.detalle}
                        </span>
                      </span>
                      <span className="mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium">
                        {TEXTOS_DE_ELEGIR_TIENDA.boton}
                      </span>
                    </button>
                  </li>
                )}
                {MISIONES.map((m) => {
                  const vista = vistaDeMision(m, progreso, tour);
                  const hecha = vista.estado === 'completa';
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => ir(m)}
                        className={cn(
                          'flex min-h-11 w-full items-start gap-3 rounded-xl px-2 py-2 text-left hover:bg-muted',
                          siguiente?.id === m.id && 'bg-primary/5'
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                            hecha
                              ? 'border-transparent bg-degradado-prixia text-white'
                              : vista.estado === 'en-curso'
                                ? 'border-primary'
                                : 'border-muted-foreground/40'
                          )}
                        >
                          {hecha && <Check className="h-3 w-3" aria-hidden="true" />}
                          {vista.estado === 'en-curso' && (
                            <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block text-[13px] font-medium',
                              hecha && 'text-muted-foreground line-through'
                            )}
                          >
                            {m.titulo}
                          </span>
                          <span className="sr-only">{ESTADO_EN_PALABRAS[vista.estado]}.</span>
                          <span className="block text-xs text-muted-foreground">{m.detalle}</span>
                          {vista.falta && (
                            <span className="mt-0.5 block text-xs font-medium text-primary">
                              {vista.falta}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium">
                          {vista.boton}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              <div className="border-t px-4 py-3">
                {sinTienda ? (
                  <button
                    type="button"
                    onClick={elegirTienda}
                    className="min-h-11 w-full rounded-xl bg-degradado-prixia px-3 py-2 text-sm font-semibold text-white"
                  >
                    {TEXTOS_DE_ELEGIR_TIENDA.siguiente}
                  </button>
                ) : siguiente ? (
                  <button
                    type="button"
                    onClick={() => ir(siguiente)}
                    className="min-h-11 w-full rounded-xl bg-degradado-prixia px-3 py-2 text-sm font-semibold text-white"
                  >
                    Siguiente: {siguiente.titulo} →
                  </button>
                ) : (
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold">
                      <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                      Tu Prixia está lista
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Terminaste el recorrido de Compite.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Orden sugerido: la misión se puede hacer igual, pero primero se ofrece la previa. */}
      <Dialog open={primero !== null} onOpenChange={(a) => !a && setPrimero(null)}>
        <DialogContent className="max-w-sm">
          {primero && (
            <>
              <DialogTitle>{textoDePrimero(primero.previa)}</DialogTitle>
              <DialogDescription>
                «{primero.mision.titulo}» sale mejor después de «{primero.previa.titulo}».
              </DialogDescription>
              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => arrancar(primero.previa)}
                  className="w-full rounded-xl bg-degradado-prixia px-3 py-2 text-sm font-semibold text-white"
                >
                  Abrir «{primero.previa.titulo}»
                </button>
                <button
                  type="button"
                  onClick={() => arrancar(primero.mision)}
                  className="w-full rounded-xl border px-3 py-2 text-sm font-medium hover:bg-muted"
                >
                  Ver este recorrido igual
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
